if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        this.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '') || 'fn-shared-quick-look';
        if (this.parentElement !== document.body) document.body.appendChild(this);
      }

      hide(preventFocus = false) {
        this.loadController?.abort();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (preventFocus) this.openedBy = null;
        super.hide();
        this.modalContent.innerHTML = '';
      }

      async show(opener) {
        if (opener.getAttribute('aria-disabled') === 'true') return;
        this.loadController?.abort();
        const controller = new AbortController();
        this.loadController = controller;
        const spinner = opener.querySelector('.loading__spinner');
        const errorPanel = this.querySelector('[data-quick-look-error]');
        if (errorPanel) {
          errorPanel.hidden = true;
          errorPanel.classList.add('hidden');
        }
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        spinner?.classList.remove('hidden');

        try {
          const productUrl = opener.getAttribute('data-product-url');
          const requestUrl = new URL(productUrl, window.location.origin);
          const priceVariant = opener.dataset.minPriceVariant;
          if (priceVariant) requestUrl.searchParams.set('variant', priceVariant);
          const response = await fetch(requestUrl.toString(), { signal: controller.signal });
          if (!response.ok) throw new Error('Quick Look unavailable');
          const responseHTML = new DOMParser().parseFromString(await response.text(), 'text/html');
          const productElement = responseHTML.querySelector('product-info');
          if (!productElement) throw new Error('Product information unavailable');
          responseHTML.querySelectorAll('link[rel="stylesheet"]').forEach((stylesheet) => {
            if (!stylesheet.href.includes('/compiled_assets/')) return;
            const loaded = [...document.querySelectorAll('link[rel="stylesheet"]')]
              .some((existing) => existing.href === stylesheet.href);
            if (!loaded) productElement.prepend(stylesheet.cloneNode(true));
          });
          if (controller.signal.aborted) return;
          this.modalContent.className = 'quick-add-modal__content-info';
          this.preprocessHTML(productElement);
          HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
          const label = opener.dataset.quickLookLabel;
          if (label) this.querySelector('[role="dialog"]').setAttribute('aria-label', label);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
          super.show(opener);
        } catch (error) {
          if (controller.signal.aborted) return;
          if (errorPanel) {
            this.modalContent.innerHTML = '';
            errorPanel.querySelector('[data-quick-look-fallback]').href = opener.getAttribute('data-product-url');
            errorPanel.hidden = false;
            errorPanel.classList.remove('hidden');
            super.show(opener);
          }
        } finally {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        productElement.querySelectorAll('.fn-quick-description, .fn-quick-brand').forEach((node) => node.remove());
        productElement.querySelectorAll('.quick-add-hidden').forEach((node) => node.classList.remove('quick-add-hidden'));
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        productElement.querySelectorAll('.product-popup-modal__opener').forEach((node) => node.remove());
        productElement.querySelectorAll('modal-opener[data-modal^="#ProductModal-"]').forEach((opener) => {
          const button = opener.querySelector('button');
          if (button) {
            button.querySelector('.product__media-icon')?.remove();
            button.replaceWith(...button.childNodes);
          }
        });

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

